import {devtools} from 'zustand/middleware';
import {shallow} from 'zustand/shallow';
import {useStoreWithEqualityFn} from 'zustand/traditional';
import {createStore} from 'zustand/vanilla';

import {createSiteLoginSlice} from 'app/components/header/profile/store/slice';
import type {SiteLoginActions, SiteLoginStoreState} from 'app/components/header/profile/store/types';
import {createSiteSearchSlice} from 'app/components/header/search/store/slice';
import type {SiteSearchActions, SiteSearchState} from 'app/components/header/search/store/types';
import {createHeaderSwitchToMobileSlice} from 'app/components/header/switchToMobile/store/slice';
import type {HeaderSwitchToMobileActions, HeaderSwitchToMobileState} from 'app/components/header/switchToMobile/store/types';
import {createNotificationBarSlice} from 'app/components/mobileHeader/components/notification/store/slice';
import type {NotificationBarActions, NotificationBarState} from 'app/components/mobileHeader/components/notification/store/types';
import {createOauthClientSlice} from 'app/components/slspOauthClient/store/slice';
import type {OauthClientActions, OauthClientSliceState} from 'app/components/slspOauthClient/store/types';
import {createSlspResourceSigningSlice} from 'app/components/slspResourceSigning/store/slice';
import type {SLSPResourceSigningActions, SLSPResourceSigningState} from 'app/components/slspResourceSigning/store/types';
import {createFloatingAlertSlice} from 'app/core/floatingAlert/store/slice';
import type {FloatingAlertActions, FloatingAlertSliceState} from 'app/core/floatingAlert/store/types';
import {createNotificationsSystemSlice} from 'app/core/notifications/store/slice';
import type {NotificationsSystemActions, NotificationsSystemState} from 'app/core/notifications/store/types';
import {isGemDevtoolsEnabled} from 'app/gemStore/devtools';
import {createAemEditSlice} from 'app/modules/aem/store/slice';
import type {AemEditActions, AemEditState} from 'app/modules/aem/store/types';
import {createLazyLoadPageSlice} from 'app/modules/lazyLoadPage/store/slice';
import type {LazyLoadPageActions, LazyLoadPageState} from 'app/modules/lazyLoadPage/store/types';
import {createModalComponentSlice} from 'app/modules/modal/store/slice';
import type {ModalComponentActions, ModalComponentState} from 'app/modules/modal/store/types';
import {createPrintingSlice} from 'app/modules/printingHandler/store/slice';
import type {PrintingActions, PrintingState} from 'app/modules/printingHandler/store/types';
import {createScrollDeeplinkSlice} from 'app/modules/scrollDeeplink/slice';
import type {ScrollDeeplinkSliceActions, ScrollDeeplinkSliceState} from 'app/modules/scrollDeeplink/types';
import {createMediaBreakpointSlice} from 'app/utils/mediaBreakpoint/store/mediaBreakpointSlice';
import {createMediaInnerHeightSlice} from 'app/utils/mediaBreakpoint/store/mediaInnerHeightSlice';
import type {MediaBreakpointActions, MediaBreakpointState, MediaInnerHeightActions, MediaInnerHeightState} from 'app/utils/mediaBreakpoint/store/types';
import {createStickyStackSlice} from 'app/utils/stickyStack/store/slice';
import type {StickyStackActions, StickyStackStoreState} from 'app/utils/stickyStack/store/types';
import {createUrlStateComponentSlice} from 'app/utils/urlState/store/slice';
import type {UrlStateComponentActions, UrlStateComponentState} from 'app/utils/urlState/store/types';

const createGemStore = () =>
    createStore<GemStoreState & GemStoreActions>()(
        devtools(
            (set, get) => ({
                ...createPrintingSlice(set),
                ...createNotificationsSystemSlice(set, get),
                ...createNotificationBarSlice(set),
                ...createAemEditSlice(set),
                ...createHeaderSwitchToMobileSlice(set),
                ...createSiteLoginSlice(set, get),
                ...createSiteSearchSlice(set, get),
                ...createFloatingAlertSlice(set, get),
                ...createLazyLoadPageSlice(set),
                ...createModalComponentSlice(set),
                ...createOauthClientSlice(set),
                ...createSlspResourceSigningSlice(set, get),
                ...createUrlStateComponentSlice(set, get),
                ...createMediaBreakpointSlice(set),
                ...createMediaInnerHeightSlice(set),
                ...createStickyStackSlice(set, get),
                ...createScrollDeeplinkSlice(set, get),
            }),
            {
                enabled: isGemDevtoolsEnabled,
                name: 'gemStore',
            },
        ),
    );

export const gemStore = createGemStore();

export const useGemStore = <T,>(selector: (state: GemStoreState & GemStoreActions) => T): T => useStoreWithEqualityFn(gemStore, selector, shallow);

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DynamicSlices {}

export type GemStoreInstance = ReturnType<typeof createGemStore>;
export type GemStoreStateSnapshot = ReturnType<GemStoreInstance['getState']>;
export type GemStoreState = PrintingState &
    NotificationsSystemState &
    NotificationBarState &
    AemEditState &
    HeaderSwitchToMobileState &
    SiteLoginStoreState &
    SiteSearchState &
    FloatingAlertSliceState &
    LazyLoadPageState &
    SLSPResourceSigningState &
    MediaBreakpointState &
    MediaInnerHeightState & {modalComponent: ModalComponentState} & {urlStateComponent: UrlStateComponentState} & Partial<DynamicSlices> &
    OauthClientSliceState &
    StickyStackStoreState &
    ScrollDeeplinkSliceState;

export type GemStoreActions = PrintingActions &
    NotificationsSystemActions &
    NotificationBarActions &
    AemEditActions &
    HeaderSwitchToMobileActions &
    SiteLoginActions &
    SiteSearchActions &
    FloatingAlertActions &
    LazyLoadPageActions &
    ModalComponentActions &
    SLSPResourceSigningActions &
    MediaBreakpointActions &
    MediaInnerHeightActions &
    OauthClientActions &
    StickyStackActions &
    UrlStateComponentActions &
    ScrollDeeplinkSliceActions;
